/** Helpers for company intent questionnaire answers (v9 structured). */

import type {
  IntentAnswers,
  IntentAnswerValue,
  IntentQuestion,
  IntentStructuredAnswer,
} from "./companyIntent.types";
import { INTENT_FOLLOW_UP_MAX_CHARS } from "./companyIntent.types";
import {
  Q11_PARTNER_TYPE_ID,
  Q15_GROUP_PRIORITY_COUNTRIES,
  Q15_PRIORITY_MARKET_ID,
  getMaxSelections,
  hasAnswerGroups,
  resolveIntentSelectionMode,
} from "./intentQuestionnaireDimensions";

export type ParsedIntentAnswer = {
  selectedIds: string[];
  followUpText?: string;
  groups?: Record<string, string[]>;
  regionIds?: string[];
  conditional?: Record<string, string | string[]>;
};

export function isStructuredIntentAnswer(value: unknown): value is IntentStructuredAnswer {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  return (
    "selected" in value ||
    "groups" in value ||
    "followUpText" in value ||
    "conditional" in value ||
    "regionIds" in value
  );
}

function normalizeGroupValue(value: string | string[] | undefined | null): string[] {
  if (value == null) return [];
  if (Array.isArray(value)) {
    return value.map((id) => id.trim()).filter(Boolean);
  }
  const trimmed = value.trim();
  return trimmed ? [trimmed] : [];
}

export function parseIntentAnswer(
  value: IntentAnswerValue | undefined | null,
): ParsedIntentAnswer {
  if (value == null) {
    return { selectedIds: [] };
  }

  if (isStructuredIntentAnswer(value)) {
    const selectedIds = normalizeIntentAnswer(value.selected);
    const followUpText = value.followUpText?.trim() || undefined;

    let groups: Record<string, string[]> | undefined;
    if (value.groups && typeof value.groups === "object") {
      groups = {};
      for (const [groupId, groupValue] of Object.entries(value.groups)) {
        const normalized = normalizeGroupValue(groupValue);
        if (normalized.length > 0) {
          groups[groupId] = normalized;
        }
      }
      if (Object.keys(groups).length === 0) {
        groups = undefined;
      }
    }

    const conditional =
      value.conditional && typeof value.conditional === "object"
        ? { ...value.conditional }
        : undefined;

    const regionIds = Array.isArray(value.regionIds)
      ? value.regionIds.map((id) => id.trim()).filter(Boolean)
      : undefined;

    return {
      selectedIds,
      followUpText,
      ...(groups ? { groups } : {}),
      ...(regionIds?.length ? { regionIds } : {}),
      ...(conditional ? { conditional } : {}),
    };
  }

  return { selectedIds: normalizeIntentAnswer(value) };
}

export function normalizeIntentAnswer(value: string | string[] | undefined | null): string[] {
  if (value == null) return [];
  if (Array.isArray(value)) {
    return value.map((id) => id.trim()).filter(Boolean);
  }
  const trimmed = value.trim();
  return trimmed ? [trimmed] : [];
}

function isAnswerGroupComplete(
  question: IntentQuestion,
  groupId: string,
  groupSelections: string[],
): boolean {
  const groupDef = question.answerGroups?.find((g) => g.id === groupId);
  if (!groupDef) return false;

  if (groupDef.selectionMode === "single") {
    return groupSelections.length === 1;
  }
  return groupSelections.length >= 1;
}

function isSelectionCountValid(question: IntentQuestion, selectedIds: string[]): boolean {
  const mode = resolveIntentSelectionMode(question.id, question.selectionMode);
  if (selectedIds.length === 0) return false;
  if (mode === "single" && selectedIds.length !== 1) return false;

  const maxSelections = getMaxSelections(question.id);
  if (maxSelections != null && selectedIds.length > maxSelections) {
    return false;
  }

  return true;
}

/** Full completion check respecting selection mode, answer groups and max selections. */
export function isIntentQuestionComplete(
  question: IntentQuestion,
  value: IntentAnswerValue | undefined | null,
): boolean {
  const parsed = parseIntentAnswer(value);

  if (hasAnswerGroups(question.id)) {
    const groups = parsed.groups ?? {};

    if (question.id === Q15_PRIORITY_MARKET_ID) {
      const hasRegionBand = isSelectionCountValid(question, parsed.selectedIds);
      const hasCatalogRegions = (parsed.regionIds?.length ?? 0) > 0;
      const countryIds = groups[Q15_GROUP_PRIORITY_COUNTRIES] ?? [];
      return hasRegionBand || hasCatalogRegions || countryIds.length > 0;
    }

    if (!question.answerGroups?.length) {
      return isSelectionCountValid(question, parsed.selectedIds);
    }

    for (const group of question.answerGroups) {
      const selections = groups[group.id] ?? [];
      if (!isAnswerGroupComplete(question, group.id, selections)) {
        return false;
      }
    }

    return true;
  }

  return isSelectionCountValid(question, parsed.selectedIds);
}

/** Dedupe option IDs while preserving first-seen order. Single mode keeps at most one. */
export function serializeIntentAnswer(
  ids: string[],
  mode: "single" | "multiple" = "multiple",
  maxSelections?: number,
): string[] {
  let deduped = dedupeOptionIds(ids);
  if (mode === "single") {
    return deduped.length > 0 ? [deduped[deduped.length - 1]!] : [];
  }
  if (maxSelections != null && deduped.length > maxSelections) {
    deduped = deduped.slice(0, maxSelections);
  }
  return deduped;
}

function dedupeOptionIds(ids: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const id of ids) {
    const trimmed = id.trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    result.push(trimmed);
  }
  return result;
}

/** Build API-ready answer payload from UI state. */
export function toIntentAnswerPayload(params: {
  selectedIds?: string[];
  selectionMode?: "single" | "multiple";
  maxSelections?: number;
  followUpText?: string;
  groupSelections?: Record<string, string[]>;
  groupRegionIds?: Record<string, string[]>;
  conditionalAnswers?: Record<string, string | string[]>;
  usesAnswerGroups?: boolean;
}): IntentStructuredAnswer | string | string[] {
  const selectionMode = params.selectionMode ?? "multiple";
  const selected = serializeIntentAnswer(
    params.selectedIds ?? [],
    selectionMode,
    params.maxSelections,
  );
  const hasFollowUp = Boolean(params.followUpText?.trim());
  const hasGroups = Boolean(
    params.usesAnswerGroups &&
    params.groupSelections &&
    Object.values(params.groupSelections).some((ids) => ids.length > 0),
  );
  const regionIds = params.groupRegionIds
    ? Object.values(params.groupRegionIds).flat().map((id) => id.trim()).filter(Boolean)
    : [];
  const uniqueRegionIds = [...new Set(regionIds)];
  const hasRegionIds = uniqueRegionIds.length > 0;
  const hasConditional = Boolean(
    params.conditionalAnswers &&
    Object.values(params.conditionalAnswers).some((v) =>
      Array.isArray(v) ? v.length > 0 : Boolean(String(v).trim()),
    ),
  );

  if (!hasFollowUp && !hasGroups && !hasConditional && !hasRegionIds) {
    if (selectionMode === "single") {
      return selected[0] ?? "";
    }
    return selected;
  }

  const groupsPayload: Record<string, string | string[]> = {};
  if (hasGroups && params.groupSelections) {
    for (const [groupId, ids] of Object.entries(params.groupSelections)) {
      const deduped = dedupeOptionIds(ids);
      if (deduped.length === 0) continue;
      groupsPayload[groupId] = deduped;
    }
  }

  return {
    ...(selected.length > 0
      ? { selected: selectionMode === "single" ? (selected[0] ?? "") : selected }
      : {}),
    ...(hasFollowUp ? { followUpText: params.followUpText!.trim() } : {}),
    ...(Object.keys(groupsPayload).length > 0 ? { groups: groupsPayload } : {}),
    ...(hasRegionIds ? { regionIds: uniqueRegionIds } : {}),
    ...(hasConditional ? { conditional: params.conditionalAnswers } : {}),
  };
}

export function getAnswerGroupSelections(
  _question: IntentQuestion,
  value: IntentAnswerValue | undefined | null,
): Record<string, string[]> {
  const parsed = parseIntentAnswer(value);
  return { ...(parsed.groups ?? {}) };
}

export function collectIntentFollowUpTexts(
  answers: Record<string, { followUpText?: string | null } | undefined>,
): string {
  const seen = new Set<string>();
  const parts: string[] = [];
  for (const state of Object.values(answers)) {
    const text = state?.followUpText?.trim();
    if (!text || seen.has(text)) continue;
    seen.add(text);
    parts.push(text);
  }
  return parts.join("\n").slice(0, INTENT_FOLLOW_UP_MAX_CHARS);
}

export function collapseQuestionnaireFollowUpAnswers(
  answers: IntentAnswers,
  questionIds: readonly string[],
): IntentAnswers {
  const collected = collectIntentFollowUpTexts(
    Object.fromEntries(
      Object.entries(answers).map(([id, value]) => [
        id,
        { followUpText: parseIntentAnswer(value).followUpText ?? null },
      ]),
    ),
  );
  const targetId = questionIds.includes(Q15_PRIORITY_MARKET_ID)
    ? Q15_PRIORITY_MARKET_ID
    : questionIds[questionIds.length - 1];
  if (!targetId) return answers;

  const next: IntentAnswers = {};
  for (const [id, value] of Object.entries(answers)) {
    if (isStructuredIntentAnswer(value)) {
      const { followUpText: _drop, ...rest } = value;
      next[id] =
        id === targetId && collected
          ? { ...rest, followUpText: collected }
          : (rest as IntentAnswerValue);
      continue;
    }
    if (id === targetId && collected) {
      const parsed = parseIntentAnswer(value);
      next[id] = {
        selected:
          parsed.selectedIds.length <= 1
            ? (parsed.selectedIds[0] ?? "")
            : parsed.selectedIds,
        followUpText: collected,
      };
      continue;
    }
    next[id] = value;
  }
  return next;
}

export { Q11_PARTNER_TYPE_ID };
